// src/modules/LocationMovements/hooks/useProcessedFilters.ts

import { useMemo } from 'react';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import type { AgGridTableProps } from '@shared/components/ui/AgGridTable';

type SortingConfig = NonNullable<AgGridTableProps['sorting']>;

export const useProcessedFilters = (filterConditions: FilterConditionOutput[], sorting: SortingConfig | null) => {
  return useMemo(() => {
    // 🎯 Construir advanced_filters para la API
    const advancedFilters = {
      filters: [] as Array<{
        column: string;
        operator: string;
        value: string | string[] | number | number[];
      }>,
      logic: 'AND' as 'AND' | 'OR',
    };

    // 🎯 Campos numéricos que requieren conversión de string a number
    const numericColumns = [
      'product_id',
      'user_id',
      'event_id',
      'qty',
      'quantity',
      'from_location_id',
      'to_location_id',
      'source_location_id',
      'target_location_id',
      'team_id',
      'order_id',
      'stock_location_order_id',
      'returned_reception_id',
      'entry_id',
      'extra_work_order_id',
      'location_order_id',
      'batch_id',
    ];

    // Procesar cada condición de filtro
    filterConditions.forEach((condition) => {
      const { column, action, value } = condition;
      const apiColumn = column; // Ahora directamente coincide con la API
      const apiOperator = action; // Ahora directamente coincide con la API

      // 🎯 Verificar si es un campo numérico
      const isNumericColumn = numericColumns.includes(apiColumn);

      let processedValue: string | string[] | number | number[] = value;

      if (action === 'in' || action === 'not_in') {
        const arrayValue = Array.isArray(value) ? value : [value];
        // Convertir a números si es un campo numérico
        processedValue = isNumericColumn ? arrayValue.map((v) => (typeof v === 'string' ? Number(v) : v)) : arrayValue;
      } else if (action === 'is_null' || action === 'is_not_null') {
        advancedFilters.filters.push({
          column: apiColumn,
          operator: apiOperator,
        });
        return;
      } else if (action === 'between' && Array.isArray(value) && value.length === 2) {
        processedValue = `${value[0]},${value[1]}`;
      } else {
        const singleValue = Array.isArray(value) ? value[0] : value;
        // Convertir a número si es un campo numérico
        processedValue = isNumericColumn && typeof singleValue === 'string' ? Number(singleValue) : singleValue;
      }

      advancedFilters.filters.push({
        column: apiColumn,
        operator: apiOperator,
        value: processedValue,
      });
    });

    return {
      ...(advancedFilters.filters.length > 0 && {
        advanced_filters: advancedFilters,
      }),
      sortBy: sorting?.field ?? 'id',
      sortDirection: sorting?.direction ?? 'desc',
    };
  }, [filterConditions, sorting]);
};
